// Welcome purchase offer (SS-13): pure rules shared by the customer banner and
// the admin screens. The server decides who gets an offer; these only format
// it and mirror the server's input limits.

export const OFFER_PERCENT_MIN = 1;
export const OFFER_PERCENT_MAX = 100;
export const OFFER_DAYS_MIN = 1;
export const OFFER_DAYS_MAX = 90;
export const OFFER_CODE_MAX = 32;
export const OFFER_EXTEND_DAYS_MIN = 1;
export const OFFER_EXTEND_DAYS_MAX = 30;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// msLeft is the time until endsAt (RFC3339); <= 0 (or NaN) once it has passed.
export const offerMsLeft = (endsAt, now = Date.now()) => new Date(endsAt).getTime() - now;

// offerCountdown: "3 days" while a day or more is left, else "5 hours 12 minutes"
// / "12 minutes"; '' once the offer has ended (or endsAt is not a date).
export const offerCountdown = (endsAt, now = Date.now()) => {
  const left = offerMsLeft(endsAt, now);
  if (!(left > 0)) {
    return '';
  }
  if (left >= DAY) {
    return plural(Math.floor(left / DAY), 'day');
  }
  const totalMinutes = Math.ceil(left / MINUTE);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return plural(minutes, 'minute');
  }
  return minutes === 0 ? plural(hours, 'hour') : `${plural(hours, 'hour')} ${plural(minutes, 'minute')}`;
};

const isWholeIn = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

// validateOfferConfig checks the onboarding default offer before saving.
// Returns '' when valid, else a message for the admin.
export const validateOfferConfig = ({enabled, percent, days, code}) => {
  if (!enabled) {
    return '';
  }
  if (!isWholeIn(Number(percent), OFFER_PERCENT_MIN, OFFER_PERCENT_MAX)) {
    return `Offer percent must be a whole number from ${OFFER_PERCENT_MIN} to ${OFFER_PERCENT_MAX}.`;
  }
  if (!isWholeIn(Number(days), OFFER_DAYS_MIN, OFFER_DAYS_MAX)) {
    return `Offer days must be a whole number from ${OFFER_DAYS_MIN} to ${OFFER_DAYS_MAX}.`;
  }
  if (String(code || '').trim().length > OFFER_CODE_MAX) {
    return `Offer code must be at most ${OFFER_CODE_MAX} characters.`;
  }
  return '';
};

// validateExtendDays: '' when days is a whole number in 1..30.
export const validateExtendDays = (days) => (
  isWholeIn(Number(days), OFFER_EXTEND_DAYS_MIN, OFFER_EXTEND_DAYS_MAX)
    ? ''
    : `Enter a whole number of days from ${OFFER_EXTEND_DAYS_MIN} to ${OFFER_EXTEND_DAYS_MAX}.`
);

// offerStatus for an org's offer as support sees it: 'disabled' | 'expired' | 'active'.
// The server's `active` (enabled and not expired) decides; protojson omits
// false booleans, so a missing flag means false.
export const offerStatus = (offer) => {
  if (!offer.enabled) {
    return 'disabled';
  }
  return offer.active ? 'active' : 'expired';
};
